import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Pill, Search, PackageX } from "lucide-react";
import AddToCartBtn, { unitTextOf } from "../../storefront/AddToCartBtn";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StoreShop() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState(params.get("q") || "");
  const cat = params.get("cat") || "All";
  const sub = params.get("sub") || "";
  const [catTree, setCatTree] = useState([]);

  useEffect(() => {
    const qp = params.get("q");
    if (qp !== null) setSearch(qp);
  }, [params]);

  useEffect(() => {
    axios.get(`${API}/public/medicines`).then(({ data }) => setItems(data)).catch(() => setItems([]));
    axios.get(`${API}/public/categories`).then(({ data }) => setCatTree(data)).catch(() => {});
  }, []);

  const categories = useMemo(() => ["All", ...new Set((items || []).map((m) => m.category || "General"))].sort((a, b) => (a === "All" ? -1 : b === "All" ? 1 : a.localeCompare(b))), [items]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (items || []).filter((m) => {
      if (cat !== "All") {
        const mc = m.category || "General";
        if (mc !== cat && !mc.toLowerCase().includes(cat.toLowerCase()) && !m.name.toLowerCase().includes(cat.toLowerCase())) return false;
        if (sub && (m.subcategory || "") !== sub) return false;
      }
      if (s && ![m.name, m.generic_name, m.manufacturer, m.category, m.subcategory].filter(Boolean).join(" ").toLowerCase().includes(s)) return false;
      return true;
    });
  }, [items, cat, sub, search]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="store-shop-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Shop Medicines</h1>
      <p className="text-sm text-slate-500 mt-1 mb-6">Live stock hamari Kashipur dukaan se — real ₹ prices, koi fake listing nahi.</p>

      <div className="relative mb-4">
        <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          data-testid="shop-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Medicine, salt ya brand search karein… (e.g. Dolo, Azithral)"
          className="w-full h-12 pl-11 pr-4 rounded-full border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-transparent"
        />
      </div>

      <div className="md:hidden flex gap-2 overflow-x-auto pb-2 mb-6 -mx-4 px-4">
        {categories.map((c) => (
          <button key={c} data-testid={`category-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setParams(c === "All" ? {} : { cat: c })}
            className={`shrink-0 px-4 h-9 rounded-full text-xs font-semibold transition-colors ${cat === c ? "bg-[#064E3B] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-emerald-300"}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="flex gap-6 items-start">
        <aside className="hidden md:block w-52 shrink-0" data-testid="shop-category-sidebar">
          <div className="bg-white border border-slate-200 rounded-2xl p-3 sticky top-20">
            <div className="px-2 pb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">Categories</div>
            <button onClick={() => setParams({})} data-testid="sidebar-cat-all"
              className={`w-full text-left px-2.5 py-2 rounded-lg text-sm font-semibold transition-colors ${cat === "All" ? "bg-[#064E3B] text-white" : "text-slate-700 hover:bg-emerald-50"}`}>
              All Products
            </button>
            {catTree.map((c) => (
              <div key={c.name} className="mt-0.5">
                <button onClick={() => setParams({ cat: c.name })} data-testid={`sidebar-cat-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`w-full text-left px-2.5 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center justify-between gap-1 ${cat === c.name && !sub ? "bg-[#064E3B] text-white" : "text-slate-700 hover:bg-emerald-50"}`}>
                  <span className="truncate">{c.name}</span>
                  {c.product_count > 0 && <span className={`text-[10px] font-bold shrink-0 ${cat === c.name && !sub ? "text-emerald-200" : "text-slate-400"}`}>{c.product_count}</span>}
                </button>
                {cat === c.name && (c.children || []).length > 0 && (
                  <div className="ml-3 mt-0.5 border-l-2 border-emerald-100 pl-2 space-y-0.5 pb-1">
                    {(c.children || []).map((s) => (
                      <button key={s.name} onClick={() => setParams({ cat: c.name, sub: s.name })} data-testid={`sidebar-sub-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`w-full text-left px-2 py-1.5 rounded-lg text-xs transition-colors flex items-center justify-between gap-1 ${sub === s.name ? "bg-emerald-100 text-emerald-900 font-bold" : "text-slate-500 hover:bg-emerald-50 hover:text-emerald-800"}`}>
                        <span className="truncate">{s.name}</span>
                        {s.product_count > 0 && <span className="text-[10px] text-slate-400 shrink-0">{s.product_count}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>
        <div className="flex-1 min-w-0">
      {items === null ? (
        <div className="text-center py-16 text-slate-400 text-sm">Stock load ho raha hai…</div>
      ) : (
        <>
          <div className="text-xs text-slate-500 mb-3" data-testid="shop-result-count">{filtered.length} products</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {filtered.map((m) => (
              <div key={m.id} data-testid="product-card-item" className="relative bg-white border border-slate-200 rounded-2xl p-4 flex flex-col hover:shadow-lg hover:border-emerald-200 transition-all">
                <Link to={`/store/medicine/${m.id}`} data-testid={`product-link-${m.id}`} aria-label={`${m.name} details`}
                  className="absolute inset-0 rounded-2xl z-0" />
                {m.image ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${m.image}`} alt={m.name} loading="lazy" className="w-full aspect-square rounded-2xl object-cover mb-3 border border-slate-100" />
                ) : (
                  <div className={`w-full aspect-square rounded-2xl flex items-center justify-center mb-3 ${m.in_stock ? "bg-emerald-50" : "bg-slate-100"}`}>
                    {m.in_stock ? <Pill className="w-10 h-10 text-emerald-600" /> : <PackageX className="w-10 h-10 text-slate-300" />}
                  </div>
                )}
                <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">{m.category}{m.subcategory ? ` › ${m.subcategory}` : ""}</div>
                <div className="font-semibold text-slate-900 text-sm leading-snug mt-0.5 flex-1">{m.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{m.generic_name || m.manufacturer || "—"}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{unitTextOf(m)}</div>
                {m.description && <div className="text-[11px] text-slate-500 mt-1 line-clamp-2 leading-relaxed" data-testid={`med-desc-${m.id}`}>{m.description}</div>}
                <div className="flex items-center justify-between mt-3">
                  {m.offer_price > 0 ? (
                    <span className="flex items-baseline gap-1.5" data-testid={`offer-price-${m.id}`}>
                      <span className="font-mono-num font-bold text-emerald-900">₹{m.offer_price.toFixed(2)}</span>
                      <span className="font-mono-num text-[11px] text-slate-400 line-through">₹{m.mrp.toFixed(2)}</span>
                    </span>
                  ) : (
                    <span className="font-mono-num font-bold text-slate-900">₹{m.mrp.toFixed(2)}</span>
                  )}
                  {m.in_stock
                    ? <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">IN STOCK</span>
                    : <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-1 rounded-full">OUT OF STOCK</span>}
                </div>
                <div className="relative z-10"><AddToCartBtn m={m} testid="product-add-to-cart-btn" /></div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full text-center py-16">
                <PackageX className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">Is naam se kuch nahi mila. WhatsApp par poochh lein — store par available ho sakta hai.</p>
              </div>
            )}
          </div>
        </>
      )}
        </div>
      </div>
    </div>
  );
}
